import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Permission definitions
const ROLE_PERMISSIONS = {
  admin: { view: true, add: true, edit: true, delete: true, accounting: true, settings: true, website: true, ai: true, payroll: true, debts: true },
  accountant: { view: true, add: true, edit: false, delete: false, accounting: true, settings: false, website: false, ai: true, payroll: true, debts: true },
  staff: { view: true, add: true, edit: false, delete: false, accounting: false, settings: false, website: false, ai: false, payroll: false, debts: false },
  viewer: { view: true, add: false, edit: false, delete: false, accounting: false, settings: false, website: false, ai: false, payroll: false, debts: false }
};

const getPermsForUser = (role, customPerms) => {
  const base = { ...(ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.viewer) };
  if (customPerms && typeof customPerms === "object") {
    Object.keys(customPerms).forEach(k => { if (k in base) base[k] = customPerms[k]; });
  }
  return base;
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(ROLE_PERMISSIONS.viewer);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem("token");
      if (savedToken && !user) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` },
            timeout: 10000
          });
          setUser(response.data);
          setToken(savedToken);
          const userRole = response.data.role || "viewer";
          setPermissions(getPermsForUser(userRole, response.data.custom_permissions));
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
          setPermissions(ROLE_PERMISSIONS.viewer);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password }, { timeout: 15000 });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    const userRole = userData.role || "viewer";
    setPermissions(getPermsForUser(userRole, userData.custom_permissions));
    return response.data;
  };

  const register = async (name, username, password, role) => {
    const response = await axios.post(`${API}/auth/register`, { name, username, password, role }, { timeout: 15000 });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    const userRole = userData.role || "viewer";
    setPermissions(getPermsForUser(userRole, userData.custom_permissions));
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setPermissions(ROLE_PERMISSIONS.viewer);
  };

  // Helper functions for checking permissions
  const canEdit = () => permissions.edit;
  const canDelete = () => permissions.delete;
  const canAdd = () => permissions.add;
  const canAccessAccounting = () => permissions.accounting;
  const canAccessSettings = () => permissions.settings;
  const isAdmin = () => user?.role === "admin";
  const isAccountant = () => user?.role === "accountant";
  const isAdminOrAccountant = () => user?.role === "admin" || user?.role === "accountant";

  const value = { 
    user, 
    token, 
    loading, 
    login, 
    register, 
    logout, 
    permissions,
    canEdit,
    canDelete,
    canAdd,
    canAccessAccounting,
    canAccessSettings,
    isAdmin,
    isAccountant,
    isAdminOrAccountant
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
